import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Sync, Build, CheckCircle, Search, Download } from '@mui/icons-material';
import customerService, { StatusSyncItem } from '../services/customerService';

interface Props {
  open: boolean;
  onClose: () => void;
  customerIds: string[];
  scopeLabel: string;
  onFixed?: () => void;
  getStatusLabel: (status: string) => string;
}

type FilterView = 'all' | 'issues';

const CustomerStatusSyncDialog: React.FC<Props> = ({
  open,
  onClose,
  customerIds,
  scopeLabel,
  onFixed,
  getStatusLabel,
}) => {
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [result, setResult] = useState<{
    total: number;
    synced: number;
    mismatched: number;
    notFound: number;
    errors: number;
    items: StatusSyncItem[];
  } | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [filterView, setFilterView] = useState<FilterView>('issues');
  const [fixingId, setFixingId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const runCheck = async (silent = false) => {
    if (!silent) setLoading(true);
    if (!silent) setMessage(null);
    try {
      const data = await customerService.checkStatusSync(customerIds);
      setResult(data);
      setMessage({
        type: data.mismatched + data.notFound + data.errors > 0 ? 'info' : 'success',
        text: `${data.synced} sinkron · ${data.mismatched} tidak sinkron · ${data.notFound} tidak ditemukan · ${data.errors} error`,
      });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Gagal mengecek sinkronisasi',
      });
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleFix = async (ids: string[], mode: 'db_to_mikrotik' | 'mikrotik_to_db', singleId?: string) => {
    if (ids.length === 0) return;
    setFixing(true);
    if (singleId) setFixingId(singleId);
    setMessage(null);
    try {
      const data = await customerService.fixStatusSync(ids, mode);
      setMessage({
        type: data.failed > 0 ? 'error' : 'success',
        text: `${data.fixed} berhasil diperbaiki${data.failed > 0 ? `, ${data.failed} gagal` : ''}`,
      });
      onFixed?.();
      await runCheck(true);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Gagal memperbaiki sinkronisasi',
      });
    } finally {
      setFixing(false);
      setFixingId(null);
    }
  };

  const issueItems = result?.items.filter((i) => i.syncStatus !== 'synced') ?? [];
  const displayItems =
    filterView === 'issues' && result ? issueItems : result?.items ?? [];

  const fixableIds = issueItems
    .filter((i) => i.syncStatus === 'mismatch')
    .map((i) => i.id);

  const mikrotikLabel = (item: StatusSyncItem) => {
    if (item.syncStatus === 'error') return item.error || 'Error';
    if (item.syncStatus === 'not_found') return 'PPP tidak ditemukan';
    if (item.mikrotikEnabled === null) return '-';
    return item.mikrotikEnabled ? 'PPP Aktif' : 'PPP Nonaktif';
  };

  const syncChip = (item: StatusSyncItem) => {
    switch (item.syncStatus) {
      case 'synced':
        return <Chip label="Sinkron" color="success" size="small" />;
      case 'mismatch':
        return <Chip label="Tidak sinkron" color="warning" size="small" />;
      case 'not_found':
        return <Chip label="PPP hilang" color="error" size="small" />;
      default:
        return <Chip label="Error" color="error" size="small" />;
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setMessage(null);
    try {
      await customerService.exportStatusSync(customerIds, {
        scopeLabel,
        filter: filterView === 'issues' ? 'issues' : 'all',
        items: result?.items,
      });
      setMessage({ type: 'success', text: 'File Excel berhasil diunduh.' });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Gagal mengekspor hasil ke Excel',
      });
    } finally {
      setExporting(false);
    }
  };

  const handleClose = () => {
    if (loading || fixing || exporting) return;
    setResult(null);
    setMessage(null);
    setFilterView('issues');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Sync />
        Cek Sinkronisasi DB ↔ MikroTik
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Membandingkan status pelanggan di database dengan PPP Secret di MikroTik.
          Scope: <strong>{scopeLabel}</strong> ({customerIds.length} pelanggan).
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          Aturan: status DB <strong>Aktif</strong> = PPP enabled di MikroTik. Status lainnya = PPP disabled.
        </Alert>

        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }}>
            {message.text}
          </Alert>
        )}

        {!result && !loading && (
          <Box textAlign="center" py={4}>
            <Search sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              Klik &quot;Mulai Cek&quot; untuk membandingkan status DB dan MikroTik.
            </Typography>
          </Box>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {result && !loading && (
          <>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
              <Chip icon={<CheckCircle />} label={`${result.synced} sinkron`} color="success" size="small" />
              <Chip label={`${result.mismatched} tidak sinkron`} color="warning" size="small" />
              <Chip label={`${result.notFound} PPP hilang`} color="error" size="small" variant="outlined" />
              <Chip label={`${result.errors} error`} color="error" size="small" variant="outlined" />
            </Stack>

            <ToggleButtonGroup
              size="small"
              value={filterView}
              exclusive
              onChange={(_, v) => v && setFilterView(v)}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="issues">Hanya bermasalah ({issueItems.length})</ToggleButton>
              <ToggleButton value="all">Semua ({result.items.length})</ToggleButton>
            </ToggleButtonGroup>

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Pelanggan</TableCell>
                    <TableCell>Status DB</TableCell>
                    <TableCell>Status MikroTik</TableCell>
                    <TableCell>Router</TableCell>
                    <TableCell>Hasil</TableCell>
                    <TableCell align="right">Perbaiki</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        {filterView === 'issues' ? 'Semua pelanggan sudah sinkron' : 'Tidak ada data'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {item.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.customer_code} · {item.username}
                          </Typography>
                        </TableCell>
                        <TableCell>{getStatusLabel(item.dbStatus)}</TableCell>
                        <TableCell>{mikrotikLabel(item)}</TableCell>
                        <TableCell>{item.routerName || '-'}</TableCell>
                        <TableCell>{syncChip(item)}</TableCell>
                        <TableCell align="right">
                          {item.syncStatus === 'mismatch' && (
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <Tooltip title="Sesuaikan MikroTik mengikuti DB">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={fixing}
                                    onClick={() => handleFix([item.id], 'db_to_mikrotik', item.id)}
                                  >
                                    {fixingId === item.id ? (
                                      <CircularProgress size={18} />
                                    ) : (
                                      <Build fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Sesuaikan DB mengikuti MikroTik">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="secondary"
                                    disabled={fixing}
                                    onClick={() => handleFix([item.id], 'mikrotik_to_db', item.id)}
                                  >
                                    <Sync fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={handleClose} disabled={loading || fixing || exporting}>
          Tutup
        </Button>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <Search />}
          onClick={() => runCheck()}
          disabled={loading || fixing || exporting || customerIds.length === 0}
        >
          {loading ? 'Mengecek...' : result ? 'Cek Ulang' : 'Mulai Cek'}
        </Button>
        {result && (
          <Button
            variant="outlined"
            color="success"
            startIcon={exporting ? <CircularProgress size={16} /> : <Download />}
            onClick={handleExport}
            disabled={loading || fixing || exporting}
          >
            {exporting ? 'Mengekspor...' : `Export Excel (${filterView === 'issues' ? 'bermasalah' : 'semua'})`}
          </Button>
        )}
        {result && fixableIds.length > 0 && (
          <>
            <Button
              variant="contained"
              startIcon={fixing ? <CircularProgress size={16} color="inherit" /> : <Build />}
              onClick={() => handleFix(fixableIds, 'db_to_mikrotik')}
              disabled={fixing}
            >
              Perbaiki semua (ikut DB)
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={fixing ? <CircularProgress size={16} color="inherit" /> : <Sync />}
              onClick={() => handleFix(fixableIds, 'mikrotik_to_db')}
              disabled={fixing}
            >
              Perbaiki semua (ikut MikroTik)
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CustomerStatusSyncDialog;
